import React, { useEffect, useRef } from 'react';
import { useBoolean, useStateful } from 'react-hanger';
import cls from 'classnames';
import isArray from 'lodash/isArray';

import { hooks } from '@/lib';
import { UiScroll, ListEmpty } from '@/components';
import { ItemOptions, ItemProps } from '../types';
import Styled from './styled';

const Select = (
  props: ItemProps & {
    options?: any;
    dark?: boolean;
    multi?: boolean;
    autoSelFirst?: boolean;
    optionsHolder?: string;
    renderOption?: (item: any) => any;
    fetchOptions?: () => Promise<ItemOptions>;
    checkSelectable?: (value, options) => boolean;
  },
) => {
  const {
    className,
    placeholder,
    label,
    name,
    value,
    onChange,
    multi,
    renderOption,
    dark = true,
    options: propsOptions,
    optionsHolder,
    checkSelectable,
    fetchOptions: propFetchOptions,
    autoSelFirst = true,
  } = props;

  const showOptions = useBoolean(false);
  const wrapRef = useRef(null);
  const isPropOptions = isArray(propsOptions);
  const options = useStateful(propsOptions || []);

  // 选择 的选项
  const selectItemMap = {};
  const selectedItems = options.value.filter((item) => {
    const isSelected = multi
      ? value?.includes(item.value)
      : item.value === value;
    selectItemMap[item.value] = isSelected;
    return isSelected;
  });

  useEffect(() => {
    if (isPropOptions) {
      options.setValue(propsOptions);
    }
  }, [propsOptions]);

  hooks.useClickAway(wrapRef, showOptions.setFalse);

  // 默认选择 第一个 选项
  useEffect(() => {
    if (autoSelFirst && !selectedItems.length && options.value.length) {
      handleOnChange(options.value[0]);
    }
  }, [options, selectedItems]);

  const handleOnChange = (item) => {
    if (!item) {
      return;
    }
    if (multi) {
      const nextValue = selectItemMap[item.value]
        ? selectedItems.filter((i) => {
            return i.value !== item.value;
          })
        : selectedItems.concat(item);
      onChange(
        name,
        nextValue.map((item) => {
          return item.value;
        }),
      );
    } else {
      onChange(name, item.value);
    }
  };

  const fetchOptions = async () => {
    if (!isPropOptions && propFetchOptions) {
      return propFetchOptions().then((resOptions) => {
        options.setValue(resOptions);
        return resOptions;
      });
    }
    return [];
  };

  const doShowOptions = (selOptions: any = options) => {
    const showAble = checkSelectable
      ? checkSelectable(value, selOptions)
      : true;

    if (showAble) {
      showOptions.setTrue();
      wrapRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  const handleToggleOptions = async () => {
    const nextShow = !showOptions.value;

    if (nextShow) {
      let newOptions: any = options;
      if (!isPropOptions) {
        newOptions = await fetchOptions();
      }
      return doShowOptions(newOptions);
    }

    showOptions.setFalse();
  };

  const handleChange = (item) => {
    if (!item.disabled) {
      handleOnChange(item);
      showOptions.setFalse();
    }
  };

  const handleRemove = (item, e) => {
    handleOnChange(item);
    e.stopPropagation();
    showOptions.setFalse();
    return false;
  };

  const renderDisplayLabel = () => {
    if (!selectedItems.length) {
      return placeholder || `请选择${label}`;
    }

    if (multi) {
      return (
        <p className="form-item-input-inner">
          {selectedItems.map((item) => {
            return (
              <span
                className="text-overflow"
                key={item.value}
                onClick={(e) => {
                  return handleRemove(item, e);
                }}
              >
                {item.label} <i className="iconfont icon-guanbi-" />
              </span>
            );
          })}
        </p>
      );
    }

    return selectedItems[0].label;
  };

  return (
    <Styled.Select
      ref={wrapRef}
      className={cls('form-item', 'form-item-select', className, {
        'form-item-select-multi': multi,
      })}
    >
      <label className="form-item-label">{label}</label>
      <div
        className={cls('form-item-input', {
          selecting: showOptions.value,
          'text-holder': !selectedItems.length,
        })}
        onClick={handleToggleOptions}
      >
        {renderDisplayLabel()}
      </div>
      {showOptions.value && (
        /** @ts-ignore */
        <UiScroll
          overflowY
          margin={false}
          dark={dark}
          className="form-item-options"
        >
          {!options.value.length && (
            <ListEmpty>{optionsHolder || `暂无${label}`}</ListEmpty>
          )}
          {options.value.map((item) => {
            const isActive = selectItemMap[item.value];
            return (
              <div
                key={item.value}
                className={cls('item', {
                  active: isActive,
                  disabled: item.disabled,
                })}
                onClick={() => {
                  return handleChange(item);
                }}
              >
                {renderOption ? renderOption(item) : item.label}
              </div>
            );
          })}
        </UiScroll>
      )}
    </Styled.Select>
  );
};

export default Select;
